/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface MedicalFacilityContactProps {
  id: string;
  name: string;
  emailAddress: string | null;
  primaryPhoneNumber: string | null;
  secondaryPhoneNumber: string | null;
  smsPhoneNumber: string | null;
  faxPhoneNumber: string | null;
  notes: string | null;
  medicalFacilityId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class MedicalFacilityContactRecord extends Record<MedicalFacilityContactProps>({
  id: null as any,
  name: null as any,
  emailAddress: null as any,
  primaryPhoneNumber: null as any,
  secondaryPhoneNumber: null as any,
  smsPhoneNumber: null as any,
  faxPhoneNumber: null as any,
  notes: null as any,
  medicalFacilityId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    emailAddress: string | null;
    primaryPhoneNumber: string | null;
    secondaryPhoneNumber: string | null;
    smsPhoneNumber: string | null;
    faxPhoneNumber: string | null;
    notes: string | null;
    medicalFacilityId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): MedicalFacilityContactRecord {
    if (json == null) {
      throw new Error('MedicalFacilityContactRecord.fromJS: parameter cannot be null');
    }
    return new MedicalFacilityContactRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'MedicalFacilityContactJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'MedicalFacilityContactJson.name'),
      emailAddress: json.emailAddress,
      primaryPhoneNumber: json.primaryPhoneNumber,
      secondaryPhoneNumber: json.secondaryPhoneNumber,
      smsPhoneNumber: json.smsPhoneNumber,
      faxPhoneNumber: json.faxPhoneNumber,
      notes: json.notes,
      medicalFacilityId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'medicalFacilityId', '00000000-0000-0000-0000-000000000000'),
        'MedicalFacilityContactJson.medicalFacilityId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    emailAddress: string | null;
    primaryPhoneNumber: string | null;
    secondaryPhoneNumber: string | null;
    smsPhoneNumber: string | null;
    faxPhoneNumber: string | null;
    notes: string | null;
    medicalFacilityId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [MedicalFacilityContactRecord, Error[]] {
    if (json == null) {
      throw new Error('MedicalFacilityContactRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new MedicalFacilityContactRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'MedicalFacilityContactJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'MedicalFacilityContactJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      emailAddress: tryOrDefault(
        () => json.emailAddress,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      primaryPhoneNumber: tryOrDefault(
        () => json.primaryPhoneNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      secondaryPhoneNumber: tryOrDefault(
        () => json.secondaryPhoneNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      smsPhoneNumber: tryOrDefault(
        () => json.smsPhoneNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      faxPhoneNumber: tryOrDefault(
        () => json.faxPhoneNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notes: tryOrDefault(
        () => json.notes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      medicalFacilityId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'medicalFacilityId', '00000000-0000-0000-0000-000000000000'),
            'MedicalFacilityContactJson.medicalFacilityId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: MedicalFacilityContactProps) {
    super(props);
    if (props == null) {
      throw new Error('MedicalFacilityContactRecord constructor parameter cannot be null');
    }
  }
}

export abstract class MedicalFacilityContactViewModelBase implements MedicalFacilityContactProps {
  protected readonly _props: MedicalFacilityContactProps;
  private _json?: MedicalFacilityContactProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get emailAddress(): string | null {
    return this._props.emailAddress;
  }
  get primaryPhoneNumber(): string | null {
    return this._props.primaryPhoneNumber;
  }
  get secondaryPhoneNumber(): string | null {
    return this._props.secondaryPhoneNumber;
  }
  get smsPhoneNumber(): string | null {
    return this._props.smsPhoneNumber;
  }
  get faxPhoneNumber(): string | null {
    return this._props.faxPhoneNumber;
  }
  get notes(): string | null {
    return this._props.notes;
  }
  get medicalFacilityId(): string {
    return this._props.medicalFacilityId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: MedicalFacilityContactProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        emailAddress: this._props.emailAddress,
        primaryPhoneNumber: this._props.primaryPhoneNumber,
        secondaryPhoneNumber: this._props.secondaryPhoneNumber,
        smsPhoneNumber: this._props.smsPhoneNumber,
        faxPhoneNumber: this._props.faxPhoneNumber,
        notes: this._props.notes,
        medicalFacilityId: this._props.medicalFacilityId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllMedicalFacilityContactEntities = (state: AppState) =>
  state.entityStores.medicalFacilityContact.entities;

export const selectMedicalFacilityContactById = (id: string) => (state: AppState) =>
  state.entityStores.medicalFacilityContact.entities.get(id);

export const selectMedicalFacilityContactMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.medicalFacilityContact.meta.get(id);

export const selectMedicalFacilityContactAndMetadataById = (id: string) =>
  createSelector(
    selectMedicalFacilityContactById(id),
    selectMedicalFacilityContactMetadataById(id),
    (medicalFacilityContact, meta) => ({ medicalFacilityContact, meta }),
  );

export function selectMedicalFacilityContactEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllMedicalFacilityContactEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, MedicalFacilityContactRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectMedicalFacilityContactEntitiesByName(name: string) {
  return createSelector(selectAllMedicalFacilityContactEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectMedicalFacilityContactEntitiesByEmailAddress(emailAddress: string) {
  return createSelector(selectAllMedicalFacilityContactEntities, (entities) =>
    entities.filter((e) => e.emailAddress === emailAddress).valueSeq(),
  );
}

export function selectMedicalFacilityContactEntitiesByPrimaryPhoneNumber(
  primaryPhoneNumber: string,
) {
  return createSelector(selectAllMedicalFacilityContactEntities, (entities) =>
    entities.filter((e) => e.primaryPhoneNumber === primaryPhoneNumber).valueSeq(),
  );
}

export function selectMedicalFacilityContactEntitiesBySecondaryPhoneNumber(
  secondaryPhoneNumber: string,
) {
  return createSelector(selectAllMedicalFacilityContactEntities, (entities) =>
    entities.filter((e) => e.secondaryPhoneNumber === secondaryPhoneNumber).valueSeq(),
  );
}

export function selectMedicalFacilityContactEntitiesBySmsPhoneNumber(smsPhoneNumber: string) {
  return createSelector(selectAllMedicalFacilityContactEntities, (entities) =>
    entities.filter((e) => e.smsPhoneNumber === smsPhoneNumber).valueSeq(),
  );
}

export function selectMedicalFacilityContactEntitiesByFaxPhoneNumber(faxPhoneNumber: string) {
  return createSelector(selectAllMedicalFacilityContactEntities, (entities) =>
    entities.filter((e) => e.faxPhoneNumber === faxPhoneNumber).valueSeq(),
  );
}

export function selectMedicalFacilityContactEntitiesByNotes(notes: string) {
  return createSelector(selectAllMedicalFacilityContactEntities, (entities) =>
    entities.filter((e) => e.notes === notes).valueSeq(),
  );
}

export function selectMedicalFacilityContactEntitiesByMedicalFacilityId(medicalFacilityId: string) {
  return createSelector(selectAllMedicalFacilityContactEntities, (entities) =>
    entities.filter((e) => e.medicalFacilityId === medicalFacilityId).valueSeq(),
  );
}
//#endregion Selectors
